'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface SafetyMetric {
  month: string;
  incidents: number;
  nearMisses: number;
  safetyScore: number;
}

interface IncidentCategory {
  name: string;
  value: number;
  color: string;
}

interface SafetyPerformanceDashboardProps {
  className?: string;
}

const SafetyPerformanceDashboard = ({ className = '' }: SafetyPerformanceDashboardProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'6months' | '12months'>('12months');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const safetyData: SafetyMetric[] = [
    { month: 'Jan', incidents: 2, nearMisses: 5, safetyScore: 98 },
    { month: 'Feb', incidents: 1, nearMisses: 3, safetyScore: 99 },
    { month: 'Mar', incidents: 0, nearMisses: 4, safetyScore: 100 },
    { month: 'Apr', incidents: 1, nearMisses: 2, safetyScore: 99 },
    { month: 'May', incidents: 0, nearMisses: 3, safetyScore: 100 },
    { month: 'Jun', incidents: 2, nearMisses: 6, safetyScore: 98 },
    { month: 'Jul', incidents: 0, nearMisses: 2, safetyScore: 100 },
    { month: 'Aug', incidents: 1, nearMisses: 4, safetyScore: 99 },
    { month: 'Sep', incidents: 0, nearMisses: 1, safetyScore: 100 },
    { month: 'Oct', incidents: 1, nearMisses: 3, safetyScore: 99 },
    { month: 'Nov', incidents: 0, nearMisses: 2, safetyScore: 100 },
    { month: 'Dec', incidents: 0, nearMisses: 1, safetyScore: 100 }
  ];

  const incidentCategories: IncidentCategory[] = [
    { name: 'Vehicle Maintenance', value: 35, color: '#FFD700' },
    { name: 'Driver Fatigue', value: 25, color: '#FFA500' },
    { name: 'Road Conditions', value: 20, color: '#DAA520' },
    { name: 'Weather Related', value: 15, color: '#FF8C00' },
    { name: 'Other', value: 5, color: '#1B365D' }
  ];

  const displayData = selectedPeriod === '6months' ? safetyData.slice(-6) : safetyData;

  if (!isHydrated) {
    return (
      <section className={`py-16 lg:py-24 bg-muted ${className}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse space-y-8">
              <div className="h-8 bg-border rounded w-1/3 mx-auto" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="h-64 bg-border rounded" />
                <div className="h-64 bg-border rounded" />
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={`py-16 lg:py-24 bg-muted ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-accent/10 px-4 py-2 rounded-full mb-4">
              <Icon name="ChartBarIcon" size={20} className="text-accent" />
              <span className="font-body font-semibold text-sm text-accent">Live Performance Data</span>
            </div>
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
              Safety Performance Dashboard
            </h2>
            <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
              Real-time transparency into our safety metrics, incident reporting, and continuous improvement initiatives.
            </p>
          </div>

          {/* Period Selector */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-card rounded-lg p-1 border border-border">
              <button
                onClick={() => setSelectedPeriod('6months')}
                className={`px-6 py-2 rounded-md font-body font-semibold text-sm transition-all duration-300 ${
                  selectedPeriod === '6months' ?'bg-gradient-to-r from-accent to-secondary text-primary shadow-md' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                6 Months
              </button>
              <button
                onClick={() => setSelectedPeriod('12months')}
                className={`px-6 py-2 rounded-md font-body font-semibold text-sm transition-all duration-300 ${
                  selectedPeriod === '12months' ?'bg-gradient-to-r from-accent to-secondary text-primary shadow-md' :'text-muted-foreground hover:text-foreground'
                }`}
              >
                12 Months
              </button>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-8">
            {/* Incidents & Near Misses Chart */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-headline font-bold text-xl text-foreground mb-6">
                Incidents & Near Misses Trend
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={displayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" style={{ fontSize: '12px' }} />
                  <YAxis stroke="#6B7280" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '14px' }} />
                  <Bar dataKey="incidents" fill="#DC2626" name="Incidents" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="nearMisses" fill="#FFD700" name="Near Misses" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Safety Score Trend */}
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-headline font-bold text-xl text-foreground mb-6">
                Safety Score Performance
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={displayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" style={{ fontSize: '12px' }} />
                  <YAxis domain={[95, 100]} stroke="#6B7280" style={{ fontSize: '12px' }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: '14px' }} />
                  <Line
                    type="monotone"
                    dataKey="safetyScore"
                    stroke="#059669"
                    strokeWidth={3}
                    name="Safety Score (%)"
                    dot={{ fill: '#059669', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Incident Categories */}
          <div className="bg-card rounded-xl border border-border p-6 lg:p-8">
            <h3 className="font-headline font-bold text-xl text-foreground mb-6">
              Incident Categories Distribution
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={incidentCategories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {incidentCategories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>

              <div className="space-y-4">
                {incidentCategories.map((category, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      />
                      <span className="font-body text-sm font-semibold text-foreground">
                        {category.name}
                      </span>
                    </div>
                    <span className="font-body text-sm font-bold text-foreground">
                      {category.value}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Metrics Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mt-8">
            {[
              { label: 'Total Incidents', value: '8', change: '-20%', icon: 'ExclamationTriangleIcon', trend: 'down' },
              { label: 'Near Misses', value: '36', change: '-15%', icon: 'EyeIcon', trend: 'down' },
              { label: 'Avg Safety Score', value: '99.3%', change: '+0.5%', icon: 'ChartBarIcon', trend: 'up' },
              { label: 'Days Since Incident', value: '45', change: 'Record', icon: 'CalendarIcon', trend: 'neutral' }
            ].map((metric, index) => (
              <div key={index} className="bg-card rounded-lg border border-border p-4 lg:p-6">
                <div className="flex items-center justify-between mb-2">
                  <Icon name={metric.icon as any} size={24} className="text-accent" />
                  <span
                    className={`text-xs font-body font-semibold px-2 py-1 rounded-full ${
                      metric.trend === 'up' ?'bg-success/10 text-success'
                        : metric.trend === 'down' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}
                  >
                    {metric.change}
                  </span>
                </div>
                <div className="font-headline font-bold text-2xl lg:text-3xl text-foreground mb-1">
                  {metric.value}
                </div>
                <div className="font-body text-xs text-muted-foreground">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SafetyPerformanceDashboard;